import { useState, useCallback, useRef, type MutableRefObject } from "react";
import { DrillDownContextProps } from "./DrillDownContext";

const getActiveHTMLElement = () => {
  return document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
};

type useDrillDownContextStateParams = {
  defaultDrillDownIndex?: number;
};

export function useDrillDownContextState({
  defaultDrillDownIndex = 0,
}: useDrillDownContextStateParams) {
  const [drillDownIndex, setDrillDownIndex] =
    useState<DrillDownContextProps["index"]>(undefined);
  const [registeredIndices, setRegisteredIndices] = useState<
    DrillDownContextProps["registeredIndices"]
  >([]);
  // Track the drilldown path for back navigation
  const drillDownPath = useRef<number[]>([]);
  const [onClose, setOnClose] = useState<(() => void)[]>([]);
  const navigationTriggerFocusRef = useRef<HTMLElement | null>(null);
  const hostTriggerFocusRef = useRef<HTMLElement | null>(null);
  const shouldRestoreHostFocusRef = useRef(false);
  const hasActiveDrillDownRef = useRef(false);

  const restoreFocus = useCallback(
    (focusRef: MutableRefObject<HTMLElement | null>) => {
      requestAnimationFrame(() => {
        focusRef.current?.focus();
        focusRef.current = null;
      });
    },
    [],
  );

  const restoreNavigationFocus = useCallback(() => {
    restoreFocus(navigationTriggerFocusRef);
  }, [restoreFocus]);

  const restoreHostFocus = useCallback(() => {
    restoreFocus(hostTriggerFocusRef);
  }, [restoreFocus]);

  const back = useCallback(() => {
    const newIndex = drillDownPath.current.pop();
    // Return focus to the trigger element if we're closing the Drilldown
    if (newIndex == null && navigationTriggerFocusRef.current) {
      setDrillDownIndex(undefined);
      restoreNavigationFocus();
      return;
    }
    setDrillDownIndex(newIndex);
  }, [setDrillDownIndex, drillDownPath, restoreNavigationFocus]);

  const next = useCallback(() => {
    if (drillDownIndex == null) {
      // Set focus ref if the Drilldown is opening for the first time
      navigationTriggerFocusRef.current = getActiveHTMLElement();
      drillDownPath.current = [];
      setDrillDownIndex(defaultDrillDownIndex);
      return;
    }
    if (registeredIndices && registeredIndices[drillDownIndex + 1] == null) {
      // Closes the Drilldown if there is no next index
      drillDownPath.current = [];
      setDrillDownIndex(undefined);
      restoreNavigationFocus();
      return;
    }
    drillDownPath.current.push(drillDownIndex);
    setDrillDownIndex(drillDownIndex + 1);
  }, [
    drillDownIndex,
    setDrillDownIndex,
    defaultDrillDownIndex,
    drillDownPath,
    registeredIndices,
    restoreNavigationFocus,
  ]);

  const setIndexWithinBounds = useCallback(
    (index: number | undefined) => {
      // Invalid index, close the Drilldown
      if (
        index == null ||
        index < 0 ||
        (registeredIndices && registeredIndices[index] == null)
      ) {
        setDrillDownIndex(undefined);
        restoreNavigationFocus();
        return;
      }
      // Update the drilldown path for back navigation
      if (drillDownIndex === undefined) {
        navigationTriggerFocusRef.current = getActiveHTMLElement();
        drillDownPath.current = [];
      } else {
        drillDownPath.current.push(drillDownIndex);
      }
      setDrillDownIndex(index);
    },
    [
      drillDownIndex,
      setDrillDownIndex,
      drillDownPath,
      registeredIndices,
      restoreNavigationFocus,
    ],
  );

  const setHostTrigger = useCallback((element: HTMLElement | null) => {
    if (element == null || hostTriggerFocusRef.current != null) return;
    hostTriggerFocusRef.current = element;
  }, []);

  const onHostCloseHandler = useCallback(() => {
    const shouldResetToDefault =
      drillDownIndex != null || hasActiveDrillDownRef.current;

    drillDownPath.current = [];
    navigationTriggerFocusRef.current = null;
    shouldRestoreHostFocusRef.current = true;
    hasActiveDrillDownRef.current = false;

    if (shouldResetToDefault) {
      setDrillDownIndex(defaultDrillDownIndex);
    }
  }, [defaultDrillDownIndex, drillDownIndex]);

  const onHostClosedHandler = useCallback(() => {
    if (!shouldRestoreHostFocusRef.current) return;

    shouldRestoreHostFocusRef.current = false;
    restoreHostFocus();
  }, [restoreHostFocus]);

  if (drillDownIndex != null) {
    hasActiveDrillDownRef.current = true;
  }

  const setOnCloseForIndex = useCallback(
    (fn: () => void, index: number) => {
      setOnClose((arr) => {
        const nextOnClose = [...arr];
        nextOnClose[index] = () => {
          fn();
          setDrillDownIndex(undefined);
          restoreNavigationFocus();
        };
        return nextOnClose;
      });
    },
    [restoreNavigationFocus],
  );

  return {
    index: drillDownIndex,
    setIndex: setIndexWithinBounds,
    back,
    next,
    registeredIndices,
    setRegisteredIndices,
    onClose,
    setOnClose: setOnCloseForIndex,
    hostIsInteractive: true,
    setHostTrigger,
    onHostClose: onHostCloseHandler,
    onHostClosed: onHostClosedHandler,
  };
}
